import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useState,
} from "react";
import { apiFetch } from "../utils/api"; // Ensure this path is correct
import LoadingSpinner from "../Components/LoadingSpinner";

const initialState = {
  user: null,
  users: [],
  contacts: [],
  properties: [],
  listings: [],
};

const buildClearedSessionState = (user = null) => ({
  user,
  users: [],
  contacts: [],
  properties: [],
  listings: [],
});

const globalReducer = (state, action) => {
  switch (action.type) {
    case "SET_USER":
      const normalizedUser = {
        ...action.payload,
        userId: action.payload.userId ? String(action.payload.userId) : action.payload.userId,
        id: action.payload.id,
      };
      localStorage.setItem("loggedInUser", JSON.stringify(normalizedUser));

      if (
        !state.user ||
        String(state.user.id ?? "") !== String(normalizedUser.id ?? "") ||
        String(state.user.userId ?? "") !== String(normalizedUser.userId ?? "")
      ) {
        return buildClearedSessionState(normalizedUser);
      }

      return { ...state, user: normalizedUser };

    case "LOGOUT_USER":
      localStorage.removeItem("loggedInUser");
      localStorage.removeItem("token");

      return buildClearedSessionState(null);

    case "SET_USERS":
      return { ...state, users: action.payload };

    case "SET_CONTACTS":
      return { ...state, contacts: action.payload };

    case "SET_PROPERTIES":
      return { ...state, properties: action.payload };

    case "SET_LISTINGS":
      return { ...state, listings: action.payload };

    case "ADD_CONTACT":
      return { ...state, contacts: [...state.contacts, action.payload] };

    case "UPDATE_CONTACT":
      return {
        ...state,
        contacts: state.contacts.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
      };

    case "DELETE_CONTACT":
      return {
        ...state,
        contacts: state.contacts.filter((c) => c.id !== action.payload),
      };

    case "ADD_PROPERTY":
      return { ...state, properties: [...state.properties, action.payload] };

    case "UPDATE_PROPERTY":
      return {
        ...state,
        properties: state.properties.map((p) =>
          p.parcelNumber === action.payload.parcelNumber
            ? action.payload
            : p
        ),
      };

    case "UPDATE_USER_PROFILE":
      return {
        ...state,
        user: { ...state.user, ...action.payload },
        users: state.users.map((u) =>
          String(u.id) === String(action.payload.id)
            ? { ...u, ...action.payload }
            : u
        ),
      };

    case "CLEAR_SESSION_DATA":
      return buildClearedSessionState(state.user);

    default:
      return state;
  }
};

const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  const [state, dispatch] = useReducer(globalReducer, initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Lazy data fetchers
  const fetchContacts = async () => {
    if (!state.user?.id) return;
    try {
      const contactsData = await apiFetch("fetchContacts", {
        queryParams: { user_id: state.user.id },
      });
      dispatch({ type: "SET_CONTACTS", payload: contactsData.contacts });
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchListings = async () => {
    try {
      const listingsData = await apiFetch("fetchListings");
      dispatch({ type: "SET_LISTINGS", payload: listingsData.listings });
    } catch (err) {
      setError(err.message);
    }
  };

  // Restore user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("loggedInUser");
    if (storedUser) {
      dispatch({ type: "SET_USER", payload: JSON.parse(storedUser) });
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch global data except contacts and listings
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (state.user?.role === "Titan") {
          const usersData = await apiFetch("fetchUsers");
          dispatch({ type: "SET_USERS", payload: usersData.users });
        } else {
          dispatch({ type: "SET_USERS", payload: [] });
        }

        const propertiesData = await apiFetch("fetchProperties");
        dispatch({ type: "SET_PROPERTIES", payload: propertiesData.properties });

      } catch (err) {
        if (err.message === "Invalid token" || err.message === "Unauthorized") {
          dispatch({ type: "LOGOUT_USER" });
          // Redirect to login so the user can authenticate again
          window.location.href = "/login";
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };


    
    if (state.user?.id) {
      fetchAllData();
      } else {
      setLoading(false);
    }
  }, [state.user]);

  if (loading) return <LoadingSpinner fullScreen message="Loading app data…" />;
  if (error) return <div>Error loading app: {error}</div>;

  return (
    <GlobalContext.Provider value={{ state, dispatch, fetchContacts, fetchListings }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalState = () => useContext(GlobalContext);
